import { useEffect, useRef, useState, type ReactNode, type SelectHTMLAttributes, type InputHTMLAttributes, type TextareaHTMLAttributes, type ButtonHTMLAttributes } from 'react';
import { Link } from 'react-router-dom';
import { STATUS_TONE, label as labelOf, inr } from '@/lib/format';

export function PageHeader({ title, sub, actions, back }: { title: ReactNode; sub?: ReactNode; actions?: ReactNode; back?: string }) {
  return (
    <header className="page-header">
      <div>
        {back && <Link to={back} className="back">← Back</Link>}
        <h1>{title}</h1>
        {sub && <p className="sub">{sub}</p>}
      </div>
      {actions && <div className="actions">{actions}</div>}
    </header>
  );
}

export function Card({ title, children, actions, className = '', tone }: { title?: ReactNode; children: ReactNode; actions?: ReactNode; className?: string; tone?: string }) {
  return (
    <section className={`card ${tone ? `card-${tone}` : ''} ${className}`}>
      {(title || actions) && <div className="card-head"><h2>{title}</h2>{actions && <div className="actions">{actions}</div>}</div>}
      <div className="card-body">{children}</div>
    </section>
  );
}

export function Button({ variant = 'default', size, busy, children, ...rest }: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: 'default' | 'primary' | 'danger' | 'ghost' | 'link'; size?: 'sm'; busy?: boolean }) {
  return <button type="button" {...rest} disabled={rest.disabled || busy} className={`btn btn-${variant} ${size ? `btn-${size}` : ''} ${rest.className ?? ''}`}>{busy ? <Spinner small /> : null}{children}</button>;
}

export function Badge({ status, children }: { status?: string | null; children?: ReactNode }) {
  const tone = STATUS_TONE[status ?? ''] ?? 'muted';
  return <span className={`badge tone-${tone}`}>{children ?? labelOf(status)}</span>;
}

export function Spinner({ small }: { small?: boolean }) { return <span className={`spinner ${small ? 'spinner-sm' : ''}`} aria-label="Loading" />; }
export function Loading({ text = 'Loading…' }: { text?: string }) { return <div className="loading"><Spinner /> {text}</div>; }
export function Empty({ children = 'Nothing here yet.' }: { children?: ReactNode }) { return <div className="empty">{children}</div>; }
export function ErrorBox({ children }: { children: ReactNode }) { return <div className="errorbox" role="alert">{children}</div>; }

export function Field({ label, hint, error, children, required, className = '' }: { label?: ReactNode; hint?: ReactNode; error?: ReactNode; children: ReactNode; required?: boolean; className?: string }) {
  return (
    <label className={`field ${className}`}>
      {label && <span className="field-label">{label}{required && <span className="req"> *</span>}</span>}
      {children}
      {hint && !error && <span className="hint">{hint}</span>}
      {error && <span className="hint error">{error}</span>}
    </label>
  );
}
export function Input(props: InputHTMLAttributes<HTMLInputElement>) { return <input {...props} className={`input ${props.className ?? ''}`} />; }
export function Textarea(props: TextareaHTMLAttributes<HTMLTextAreaElement>) { return <textarea {...props} className={`input ${props.className ?? ''}`} />; }
export function Select({ children, ...props }: SelectHTMLAttributes<HTMLSelectElement>) { return <select {...props} className={`input ${props.className ?? ''}`}>{children}</select>; }

/** Rupee input that keeps a number in state and shows two decimals on blur. */
export function MoneyInput({ value, onChange, ...rest }: Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'> & { value: number | '' | null | undefined; onChange: (n: number | '') => void }) {
  const [text, setText] = useState(value == null || value === '' ? '' : String(value));
  useEffect(() => { setText(value == null || value === '' ? '' : String(value)); }, [value]);
  return <input {...rest} inputMode="decimal" className={`input money ${rest.className ?? ''}`} value={text}
    onChange={(e) => { const t = e.target.value.replace(/[^0-9.\-]/g, ''); setText(t); const n = t === '' || t === '-' ? '' : Number(t); onChange(Number.isNaN(n) ? '' : n); }}
    onBlur={() => { if (text !== '' && !Number.isNaN(Number(text))) setText(Number(text).toFixed(2)); }} />;
}

export function Money({ value, className = '' }: { value: number | string | null | undefined; className?: string }) {
  const n = value == null || value === '' ? null : Number(value);
  return <span className={`money ${n != null && n < 0 ? 'neg' : ''} ${className}`}>{n == null ? '' : inr(n)}</span>;
}

export function Table({ children, className = '' }: { children: ReactNode; className?: string }) { return <div className="table-wrap"><table className={`table ${className}`}>{children}</table></div>; }

export function Tabs({ tabs, value, onChange }: { tabs: { key: string; label: ReactNode; count?: number }[]; value: string; onChange: (k: string) => void }) {
  return (
    <div className="tabs" role="tablist">
      {tabs.map((t) => <button key={t.key} type="button" role="tab" aria-selected={t.key === value} className={`tab ${t.key === value ? 'active' : ''}`} onClick={() => onChange(t.key)}>{t.label}{t.count != null && <span className="count">{t.count}</span>}</button>)}
    </div>
  );
}

export function Modal({ open, onClose, title, children, footer, wide }: { open: boolean; onClose: () => void; title?: ReactNode; children: ReactNode; footer?: ReactNode; wide?: boolean }) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => { const d = ref.current; if (!d) return; if (open && !d.open) d.showModal(); if (!open && d.open) d.close(); }, [open]);
  return (
    <dialog ref={ref} className={`modal ${wide ? 'modal-wide' : ''}`} onClose={onClose} onClick={(e) => { if (e.target === ref.current) onClose(); }}>
      <div className="modal-inner">
        {title && <div className="modal-head"><h2>{title}</h2><button type="button" className="toast-x" aria-label="Close" onClick={onClose}>×</button></div>}
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-foot">{footer}</div>}
      </div>
    </dialog>
  );
}

/** A yes/no confirmation with an optional reason box. */
export function Confirm({ open, onClose, onConfirm, title, text, reason, danger, busy }: { open: boolean; onClose: () => void; onConfirm: (reason: string) => void; title: ReactNode; text?: ReactNode; reason?: boolean | string; danger?: boolean; busy?: boolean }) {
  const [r, setR] = useState('');
  useEffect(() => { if (!open) setR(''); }, [open]);
  return (
    <Modal open={open} onClose={onClose} title={title} footer={<><Button variant="ghost" onClick={onClose}>Cancel</Button><Button variant={danger ? 'danger' : 'primary'} busy={busy} disabled={!!reason && !r.trim()} onClick={() => onConfirm(r)}>Confirm</Button></>}>
      {text && <p>{text}</p>}
      {reason && <Field label={typeof reason === 'string' ? reason : 'Reason'} required><Textarea rows={3} value={r} onChange={(e) => setR(e.target.value)} /></Field>}
    </Modal>
  );
}

export function KV({ rows }: { rows: [ReactNode, ReactNode][] }) {
  return <dl className="kv">{rows.map(([k, v], i) => <div key={i}><dt>{k}</dt><dd>{v}</dd></div>)}</dl>;
}

export function Stat({ label, value, sub, tone }: { label: ReactNode; value: ReactNode; sub?: ReactNode; tone?: string }) {
  return <div className={`stat ${tone ? `tone-${tone}` : ''}`}><div className="stat-label">{label}</div><div className="stat-value">{value}</div>{sub && <div className="stat-sub">{sub}</div>}</div>;
}

export function Flags({ flags }: { flags?: { code: string; text: string }[] | null }) {
  if (!flags || flags.length === 0) return null;
  return <ul className="flags">{flags.map((f, i) => <li key={i} className={`flag ${f.code.includes('over_limit') || f.code === 'review_195' ? 'tone-danger' : 'tone-warn'}`}>{f.text}</li>)}</ul>;
}
